import { makeAutoObservable } from "mobx";
import { injectable, inject } from "inversify";
import type { Session } from "next-auth";
import type {
  CurrentUser,
  IAuthStore,
  IQueryClient,
  SessionStatus,
  TQueryClientFactory,
} from "@workspace/types";
import { getCurrentUser } from "@workspace/api";
import { ServiceID } from "./symbols";

@injectable()
export class AuthStore implements IAuthStore {
  session: Session | null = null;
  status: SessionStatus = "loading";

  private currentUserQuery: IQueryClient<CurrentUser>;

  constructor(
    @inject(ServiceID.QueryClientFactory)
    private queryClientFactory: TQueryClientFactory,
  ) {
    makeAutoObservable(this, {}, { autoBind: true });

    this.currentUserQuery = this.queryClientFactory<CurrentUser>(() => ({
      queryKey: ["current-user"],
      queryFn: getCurrentUser,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      staleTime: 5 * 60 * 1000,
      enabled: !!(this.isAuthenticated && this.user?.id),
    }));
  }

  setSession(session: Session | null, status: SessionStatus) {
    this.session = session;
    this.status = status;
  }

  clearSession() {
    this.session = null;
    this.status = "unauthenticated";
  }

  get isAuthenticated() {
    return this.status === "authenticated" && !!this.session;
  }

  get isLoading() {
    return this.status === "loading";
  }

  get user() {
    return this.session?.user;
  }

  get accessToken() {
    return this.session?.accessToken;
  }

  get currentUser(): CurrentUser | undefined {
    return this.currentUserQuery.result.data;
  }

  get isCurrentUserSettled(): boolean {
    const result = this.currentUserQuery.result;
    return result.isSuccess || result.isError;
  }
}
